import React from "react";
import { Link } from "react-router-dom";
import { Avatar } from "../../../design/avatar";
import { Badge } from "../../../design/badge";
import { getInitials } from "../../../utils/string";
import { Parent, CustodyStatus, Case } from "@visitproof/types";
import {
  PhoneIcon,
  EnvelopeIcon,
  FolderIcon,
} from "@heroicons/react/20/solid";

interface ParentGridProps {
  parents: Parent[];
  cases: Case[];
}

const ParentGrid: React.FC<ParentGridProps> = ({ parents, cases }) => {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {parents.map((parent) => (
        <ParentCard key={parent.id} parent={parent} cases={cases} />
      ))}
    </div>
  );
};

interface ParentCardProps {
  parent: Parent;
  cases: Case[];
}

const ParentCard: React.FC<ParentCardProps> = ({ parent, cases }) => {
  const caseItem = cases.find((c) => c.id === parent.caseId);
  const fullName = `${parent.firstName} ${parent.lastName}`;

  const getCustodyBadgeColor = (status: CustodyStatus | string) => {
    switch (status) {
      case CustodyStatus.Custodial:
        return "green";
      case CustodyStatus.NonCustodial:
        return "amber";
      default:
        return "zinc";
    }
  };

  const getCustodyLabel = (status: CustodyStatus | string) => {
    switch (status) {
      case CustodyStatus.Custodial:
        return "Custodial";
      case CustodyStatus.NonCustodial:
        return "Non-Custodial";
      default:
        return status || "Unknown";
    }
  };

  return (
    <Link
      to={`/parent/${parent.id}`}
      className="block p-5 transition-all duration-200 bg-white border shadow-sm group rounded-xl hover:shadow-md hover:border-blue-300 dark:bg-zinc-900 dark:border-zinc-700 dark:hover:border-blue-600"
    >
      {/* Header with Avatar */}
      <div className="flex items-start gap-4 mb-4">
        <Avatar
          src={parent.avatarUrl}
          className="size-14 ring-2 ring-zinc-100 dark:ring-zinc-800"
          initials={getInitials(fullName)}
        />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold truncate transition-colors text-zinc-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
            {fullName}
          </h3>
          {parent.custodyStatus && (
            <Badge
              color={getCustodyBadgeColor(parent.custodyStatus)}
              className="mt-1"
            >
              {getCustodyLabel(parent.custodyStatus)}
            </Badge>
          )}
        </div>
      </div>

      {/* Contact Info */}
      <div className="mb-4 space-y-2">
        {parent.phoneNumber && (
          <div className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
            <PhoneIcon className="w-4 h-4 text-zinc-400" />
            <span className="truncate">{parent.phoneNumber}</span>
          </div>
        )}
        {parent.email && (
          <div className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
            <EnvelopeIcon className="w-4 h-4 text-zinc-400" />
            <span className="truncate">{parent.email}</span>
          </div>
        )}
      </div>

      {/* Case Info */}
      <div className="pt-3 border-t border-zinc-100 dark:border-zinc-800">
        <div className="flex items-center gap-2 text-sm">
          <FolderIcon className="w-4 h-4 text-zinc-400" />
          <span className="text-zinc-600 dark:text-zinc-400">Case:</span>
          <span className="font-medium truncate text-zinc-900 dark:text-white">
            {caseItem?.identifier || parent.caseId || "Unassigned"}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ParentGrid;
